import React, { useMemo, useState } from "react";
import {
  Box, Typography, Chip, IconButton, Tooltip, LinearProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, MenuItem, TextField, Switch, FormControlLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { toast } from "react-toastify";
import { updateTenant } from "./api/tenantApi";

const INK = "#0B2A4A", BLUE = "#1E6FE8", GREEN = "#12A150", MUTED = "#64748b", LINE = "#e3ebf3", AMBER = "#B7791F", RED = "#C0362C";
const DAY = 24 * 60 * 60 * 1000;
const TIERS = ["Starter", "Growth", "Professional", "Enterprise"];
const SOON_DAYS = 14;

const fmt = (n) => (n == null ? "0" : Number(n).toLocaleString());
const fmtDate = (v) => { if (!v) return "—"; try { const d = new Date(v); return isNaN(d) ? "—" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return "—"; } };
const toInput = (v) => { if (!v) return ""; try { const d = new Date(v); return isNaN(d) ? "" : d.toISOString().slice(0, 10); } catch { return ""; } };

// Status of the subscription/trial period end.
const endInfo = (periodEndsAt) => {
  if (!periodEndsAt) return { label: "—", color: MUTED, soon: false, expired: false, days: null };
  const d = new Date(periodEndsAt);
  if (isNaN(d)) return { label: "—", color: MUTED, soon: false, expired: false, days: null };
  const days = Math.ceil((d - Date.now()) / DAY);
  if (days < 0) return { label: `Expired ${Math.abs(days)}d ago`, color: RED, soon: false, expired: true, days };
  if (days <= SOON_DAYS) return { label: `${days}d left`, color: AMBER, soon: true, expired: false, days };
  return { label: `${days}d left`, color: GREEN, soon: false, expired: false, days };
};

function EditDialog({ open, tenant, onClose, onSaved }) {
  const [tier, setTier] = useState("");
  const [isPaid, setIsPaid] = useState(false);
  const [periodEndsAt, setPeriodEndsAt] = useState("");
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (tenant) {
      setTier(tenant.tier || "");
      setIsPaid(!!tenant.isPaid);
      setPeriodEndsAt(toInput(tenant.periodEndsAt));
    }
  }, [tenant]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        tier: tier || null,
        isPaid,
        periodEndsAt: periodEndsAt ? new Date(periodEndsAt + "T23:59:59").toISOString() : null,
      };
      await updateTenant(tenant.id, payload);
      toast.success("Subscription updated");
      onSaved({ ...tenant, ...payload });
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Update failed");
    } finally { setSaving(false); }
  };

  const quickSet = (days) => {
    const d = new Date(Date.now() + days * DAY);
    setPeriodEndsAt(d.toISOString().slice(0, 10));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>Subscription — {tenant?.name}</DialogTitle>
      <DialogContent dividers>
        <TextField select label="Tier" value={tier} onChange={(e) => setTier(e.target.value)} fullWidth size="small" margin="dense">
          <MenuItem value=""><em>None</em></MenuItem>
          {TIERS.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
        <FormControlLabel sx={{ mt: 1 }} control={<Switch checked={isPaid} onChange={(e) => setIsPaid(e.target.checked)} />} label={isPaid ? "Paid" : "Trial"} />
        <TextField type="date" label="Period ends" value={periodEndsAt} onChange={(e) => setPeriodEndsAt(e.target.value)} fullWidth size="small" margin="dense" InputLabelProps={{ shrink: true }} helperText="Trial or renewal end date" />
        <Box sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
          {[14, 30, 90, 365].map((d) => (
            <Chip key={d} label={`+${d}d`} size="small" onClick={() => quickSet(d)} sx={{ cursor: "pointer" }} />
          ))}
          <Chip label="Clear" size="small" onClick={() => setPeriodEndsAt("")} sx={{ cursor: "pointer" }} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button onClick={save} variant="contained" disabled={saving} sx={{ textTransform: "none", background: BLUE }}>{saving ? "Saving…" : "Save"}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function AccountStatusTable({ tenants, onUpdated }) {
  const [edit, setEdit] = useState(null);

  const rows = useMemo(() => {
    const withInfo = (tenants || []).map((t) => ({ t, info: endInfo(t.periodEndsAt) }));
    // Sort: expired first, then ending-soonest, then dated, then undated.
    return withInfo.sort((a, a2) => {
      const da = a.info.days, db = a2.info.days;
      if (da == null && db == null) return String(a.t.name || "").localeCompare(String(a2.t.name || ""));
      if (da == null) return 1;
      if (db == null) return -1;
      return da - db;
    });
  }, [tenants]);

  const endingSoon = rows.filter((r) => r.info.soon || r.info.expired).length;
  const GRID = "2fr 1fr 0.9fr 1.1fr 1.2fr 1fr 44px";

  return (
    <Box>
      {endingSoon > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, background: "#FBF3E4", border: `1px solid #F0DFBE`, borderRadius: "10px", px: 2, py: 1.1, mb: 2 }}>
          <WarningAmberIcon sx={{ color: AMBER, fontSize: 20 }} />
          <Typography sx={{ fontSize: 13.5, color: "#7a5b1a", fontWeight: 600 }}>
            {endingSoon} subscription{endingSoon === 1 ? "" : "s"} expired or ending within {SOON_DAYS} days.
          </Typography>
        </Box>
      )}

      <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "12px", overflow: "hidden" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: GRID, gap: 1, background: "#f8fafc", px: 2, py: 1.1 }}>
          {["Tenant", "Tier", "Status", "Started", "Period ends", "Usage", ""].map((h, i) => (
            <Typography key={i} sx={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", color: "#94a3b8" }}>{h}</Typography>
          ))}
        </Box>
        <Box sx={{ maxHeight: "58vh", overflowY: "auto" }}>
          {rows.length === 0 && <Typography sx={{ fontSize: 13, color: MUTED, textAlign: "center", py: 5 }}>No tenants.</Typography>}
          {rows.map(({ t, info }) => {
            const used = Number(t.counter) || 0, cap = Number(t.maxCounterLimit) || 0;
            const pct = cap ? Math.min(100, Math.round((used / cap) * 100)) : 0;
            return (
              <Box key={t.id} sx={{ display: "grid", gridTemplateColumns: GRID, gap: 1, alignItems: "center", px: 2, py: 1.25, borderTop: `1px solid #f1f5f9`, background: info.expired ? "#fdf3f2" : info.soon ? "#fdf9f0" : "transparent", "&:hover": { background: "#f9fbff" } }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.name || "—"}</Typography>
                  <Typography sx={{ fontSize: 11, color: "#94a3b8", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.id}</Typography>
                </Box>
                <Typography sx={{ fontSize: 13, color: t.tier ? INK : "#cbd5e1", fontWeight: t.tier ? 600 : 400 }}>{t.tier || "—"}</Typography>
                <Chip size="small" label={t.isPaid ? "Paid" : "Trial"} sx={{ height: 20, fontSize: 11, fontWeight: 700, color: t.isPaid ? "#15803d" : "#92610a", background: t.isPaid ? "#dcfce7" : "#fef3c7", justifySelf: "start" }} />
                <Typography sx={{ fontSize: 12.5, color: MUTED }}>{fmtDate(t.metadata?.createdAt)}</Typography>
                <Box>
                  <Typography sx={{ fontSize: 12.5, color: info.color, fontWeight: 600 }}>{info.label}</Typography>
                  {t.periodEndsAt && <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{fmtDate(t.periodEndsAt)}</Typography>}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 12, color: "#334155", mb: 0.25 }}>{fmt(used)} / {cap ? fmt(cap) : "∞"}</Typography>
                  {cap ? <LinearProgress variant="determinate" value={pct} sx={{ height: 4, borderRadius: 3, backgroundColor: "#eef2f7", "& .MuiLinearProgress-bar": { backgroundColor: pct > 85 ? AMBER : BLUE } }} /> : null}
                </Box>
                <Tooltip title="Edit subscription" arrow>
                  <IconButton size="small" onClick={() => setEdit(t)}><EditIcon fontSize="small" /></IconButton>
                </Tooltip>
              </Box>
            );
          })}
        </Box>
      </Box>

      <EditDialog open={!!edit} tenant={edit} onClose={() => setEdit(null)} onSaved={(updated) => { onUpdated && onUpdated(updated); }} />
    </Box>
  );
}
